<template>
  <div class="gu-layout">

    <!-- ── Groups panel ───────────────────────────────────────────── -->
    <div class="gu-panel">
      <v-card flat outlined class="gu-card">

        <div class="tab-toolbar d-flex align-center gap-2 pa-3">
          <v-text-field
            v-model="groupSearch"
            placeholder="Search groups…"
            prepend-inner-icon="mdi-magnify"
            outlined dense hide-details clearable
            style="max-width:260px"
          />
          <v-spacer />
          <span class="caption text--secondary mr-2">{{ allFilteredGroupItems.length }} group{{ allFilteredGroupItems.length !== 1 ? 's' : '' }}</span>
          <v-btn color="primary" depressed small v-on:click="openGroupDialog()">
            <v-icon left small>mdi-plus-thick</v-icon>New Group
          </v-btn>
          <v-btn icon small v-on:click="loadAll">
            <v-icon small>mdi-refresh</v-icon>
          </v-btn>
        </div>
        <v-divider />

        <div ref="groupTableWrapper" class="tab-table-wrapper">
          <v-data-table
            v-bind:headers="groupHeaders"
            v-bind:items="filteredGroupItems"
            v-bind:height="groupTableHeight"
            hide-default-footer
            v-bind:items-per-page="-1"
            fixed-header
            class="groups-table"
          >
            <template v-slot:item="{ item }">
              <tr class="clickable-row" v-on:click="openGroupDialog(item)">
                <td>
                  <div class="d-flex align-center gap-2">
                    <v-icon v-bind:color="item.color || '#607D8B'" small left>mdi-folder</v-icon>
                    <span class="font-weight-medium">{{ item.name }}</span>
                    <v-chip
                      v-if="item.is_servers"
                      x-small color="warning" text-color="white"
                    >
                      <v-icon x-small left>mdi-server</v-icon>view all devices
                    </v-chip>
                  </div>
                </td>
                <td>
                  <div class="d-flex align-center gap-2">
                    <v-progress-linear
                      v-bind:value="groupRatio(item.id)"
                      v-bind:color="ratioColor(groupRatio(item.id))"
                      rounded height="6"
                      style="max-width:80px"
                    />
                    <span class="caption text--secondary">{{ groupRatio(item.id) }}%</span>
                  </div>
                </td>
                <td class="caption">{{ item.box_count }}</td>
                <td class="caption">{{ item.user_count }}</td>
                <td class="caption">
                  <template v-if="groupTraffic[item.id]">
                    <span class="success--text">↓ {{ format_bytes(groupTraffic[item.id].rx) }}</span>
                    <span class="ml-2 info--text">↑ {{ format_bytes(groupTraffic[item.id].tx) }}</span>
                  </template>
                  <span v-else class="text--disabled">—</span>
                </td>
                <td class="text-right" v-on:click.stop>
                  <v-btn icon x-small class="mr-1" v-on:click="openGroupDialog(item)">
                    <v-icon small>mdi-pencil-outline</v-icon>
                  </v-btn>
                  <v-menu bottom offset-y>
                    <template v-slot:activator="{ on }">
                      <v-btn
                        icon x-small v-on="on"
                        v-bind:disabled="item.box_count > 0 || item.user_count > 0"
                      >
                        <v-icon small color="error">mdi-delete-outline</v-icon>
                      </v-btn>
                    </template>
                    <v-btn color="error" depressed small v-on:click="deleteGroup(item)">
                      <v-icon left small>mdi-delete</v-icon>Confirm delete
                    </v-btn>
                  </v-menu>
                </td>
              </tr>
            </template>

            <template v-slot:body.append>
              <tr v-if="groupDisplayCount < allFilteredGroupItems.length">
                <td v-bind:colspan="groupHeaders.length" class="text-center pa-2" style="height:36px">
                  <v-progress-circular indeterminate size="16" color="primary" />
                </td>
              </tr>
            </template>

            <template v-slot:no-data>
              <div class="text-center py-8">
                <v-icon large class="text--disabled">mdi-account-group-outline</v-icon>
                <div class="text--disabled mt-2">No groups yet</div>
              </div>
            </template>
          </v-data-table>
        </div>
      </v-card>
    </div>

    <!-- ── Users panel ────────────────────────────────────────────── -->
    <div class="gu-panel">
      <v-card flat outlined class="gu-card">

        <div class="tab-toolbar d-flex align-center gap-2 pa-3">
          <v-text-field
            v-model="userSearch"
            placeholder="Search users…"
            prepend-inner-icon="mdi-magnify"
            outlined dense hide-details clearable
            style="max-width:260px"
          />
          <v-spacer />
          <span class="caption text--secondary mr-2">{{ allFilteredUserItems.length }} user{{ allFilteredUserItems.length !== 1 ? 's' : '' }}</span>
          <v-btn color="primary" depressed small v-on:click="openUserDialog()">
            <v-icon left small>mdi-plus-thick</v-icon>New User
          </v-btn>
          <v-btn icon small v-on:click="loadAll">
            <v-icon small>mdi-refresh</v-icon>
          </v-btn>
        </div>
        <v-divider />

        <div ref="userTableWrapper" class="tab-table-wrapper">
          <v-data-table
            v-bind:headers="userHeaders"
            v-bind:items="filteredUserItems"
            v-bind:height="userTableHeight"
            hide-default-footer
            v-bind:items-per-page="-1"
            fixed-header
            class="users-table"
          >
            <template v-slot:item="{ item }">
              <tr class="clickable-row" v-on:click="openUserDialog(item)">
                <td>
                  <div class="d-flex align-center gap-2">
                    <v-icon small color="primary">mdi-account</v-icon>
                    <strong>{{ item.username }}</strong>
                  </div>
                </td>
                <td>
                  <v-chip
                    v-for="g in item.groups"
                    v-bind:key="g.id"
                    x-small dark
                    v-bind:color="g.color || 'grey'"
                    class="mr-1"
                  >{{ g.name }}</v-chip>
                </td>
                <td><span class="caption text--secondary">{{ dates_date(item.created) }}</span></td>
                <td class="text-right" v-on:click.stop>
                  <v-btn icon x-small class="mr-1" v-on:click="openUserDialog(item)">
                    <v-icon small>mdi-pencil-outline</v-icon>
                  </v-btn>
                  <v-menu bottom offset-y>
                    <template v-slot:activator="{ on }">
                      <v-btn icon x-small v-on="on">
                        <v-icon small color="error">mdi-delete-outline</v-icon>
                      </v-btn>
                    </template>
                    <v-btn color="error" depressed small v-on:click="deleteUser(item)">
                      <v-icon left small>mdi-delete</v-icon>Confirm delete
                    </v-btn>
                  </v-menu>
                </td>
              </tr>
            </template>

            <template v-slot:body.append>
              <tr v-if="userDisplayCount < allFilteredUserItems.length">
                <td v-bind:colspan="userHeaders.length" class="text-center pa-2" style="height:36px">
                  <v-progress-circular indeterminate size="16" color="primary" />
                </td>
              </tr>
            </template>

            <template v-slot:no-data>
              <div class="text-center py-8">
                <v-icon large class="text--disabled">mdi-account-off-outline</v-icon>
                <div class="text--disabled mt-2">No users yet</div>
              </div>
            </template>
          </v-data-table>
        </div>
      </v-card>
    </div>

    <!-- ── Group dialog ──────────────────────────────────────────── -->
    <v-dialog v-model="groupDialog" max-width="500">
      <v-card>
        <v-card-title>{{ isNewGroup ? 'New group' : 'Edit group' }}</v-card-title>
        <v-card-text>
          <v-form ref="groupForm" lazy-validation>
            <v-text-field
              v-model="groupDraft.name"
              v-bind:rules="[rules.mandatory]"
              label="Name"
              outlined dense
              class="mb-2"
            />

            <div
              class="group-preview-bar mb-3"
              v-bind:style="{ background: groupDraft.color || '#607D8B' }"
            >
              {{ groupDraft.name || 'Preview' }}
            </div>

            <div class="d-flex justify-center">
              <v-color-picker
                v-model="groupDraft.color"
                flat
                hide-mode-switch
                swatches-max-height="200"
                mode="hexa"
              />
            </div>

            <template v-if="!serversGroupExists || groupDraft.is_servers">
              <v-divider />

              <v-switch
                v-model="groupDraft.is_servers"
                label="View all devices"
                persistent-hint
                class="mt-2"
              />

              <div>
                Devices in this group can connect to all other devices, in other groups too.
                <em v-if="groupDraft.is_servers">
                  Disable this function if you want to limit the visibility to the group itself.
                </em>
              </div>
            </template>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text v-on:click="saveGroup">{{ isNewGroup ? 'Create' : 'Save' }}</v-btn>
          <v-btn text v-on:click="groupDialog = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ── User dialog ───────────────────────────────────────────── -->
    <v-dialog v-model="userDialog" max-width="500">
      <v-card>
        <v-card-title>{{ isNewUser ? 'New user' : 'Edit user' }}</v-card-title>
        <v-card-text>
          <v-form ref="userForm" lazy-validation>
            <v-text-field
              v-model="userDraft.username"
              v-bind:rules="[rules.mandatory]"
              v-bind:disabled="!isNewUser"
              label="Username"
              outlined dense
              prepend-inner-icon="mdi-account-outline"
            />
            <v-text-field
              v-model="userDraft.password"
              v-bind:rules="isNewUser ? [rules.mandatory, rules.password] : [rules.passwordOptional]"
              label="Password"
              v-bind:type="showPassword ? 'text' : 'password'"
              outlined dense
              prepend-inner-icon="mdi-lock-outline"
              v-bind:append-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
              v-on:click:append="showPassword = !showPassword"
              v-bind:hint="isNewUser ? '' : 'Leave empty to keep current password'"
              class="mb-2"
              persistent-hint
            />
            <v-autocomplete
              v-model="userDraft.groupIds"
              v-bind:items="groupSelectItems"
              item-text="label"
              item-value="value"
              v-bind:rules="[rules.atLeastOneGroup]"
              label="Groups"
              outlined dense
              multiple chips small-chips deletable-chips clearable
              prepend-inner-icon="mdi-account-group"
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text v-on:click="saveUser">{{ isNewUser ? 'Create' : 'Save' }}</v-btn>
          <v-btn text v-on:click="userDialog = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </div>
</template>

<script>
const ONLINE_THRESHOLD_SECONDS = 900;
const PASSWORD_PATTERN = /^((?!.*[\s])(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&_\-*]).{8,16})$/;
const PAGE_SIZE = 25;

export default {
  name: 'GroupsUsers',

  data: () => ({
    showPassword: false,

    groups:   [],
    users:    [],
    boxes:    [],
    lastSeen: {},

    groupSearch:  null,
    userSearch:   null,
    groupTraffic: {},

    groupDisplayCount: PAGE_SIZE,
    userDisplayCount:  PAGE_SIZE,
    groupTableHeight:  400,
    userTableHeight:   400,

    groupHeaders: [
      { text: 'Name',          value: 'name' },
      { text: 'Online',        value: 'online',     width: 140,   sortable: false },
      { text: 'Devices',       value: 'box_count',  width: '10%' },
      { text: 'Users',         value: 'user_count', width: '10%' },
      { text: 'Traffic Today', value: 'traffic',    width: '20%', sortable: false },
      { text: '',              value: 'actions',    sortable: false, align: 'right', width: 100 }
    ],
    userHeaders: [
      { text: 'Username', value: 'username' },
      { text: 'Groups',   value: 'groups', width: '20%', sortable: false },
      { text: 'Created',  value: 'created', width: '20%', },
      { text: '',         value: 'actions', sortable: false, align: 'right', width: 100 }
    ],

    groupDialog: false,
    isNewGroup:  false,
    groupDraft:  { name: '', color: '#607D8B', is_servers: false },

    userDialog: false,
    isNewUser:  false,
    userDraft:  { id: null, username: '', password: '', groupIds: [] },

    rules: {
      mandatory:        v => !!v || 'Mandatory field',
      password:         v => !v || PASSWORD_PATTERN.test(v) || '8-16 chars, upper, lower, digit, special',
      passwordOptional: v => !v || PASSWORD_PATTERN.test(v) || '8-16 chars, upper, lower, digit, special',
      atLeastOneGroup:  v => (Array.isArray(v) && v.length > 0) || 'At least one group required'
    }
  }),

  computed: {
    groupSelectItems() {
      return this.groups.map(g => ({
        value: g.id,
        label: g.is_servers ? `${g.name} (view all devices)` : g.name
      }));
    },

    serversGroupExists() {
      return this.groups.some(g => g.is_servers && g.id !== this.groupDraft.id);
    },

    boxStatusMap() {
      const now = Date.now();
      return Object.keys(this.lastSeen).reduce((map, key) => {
        const entry  = this.lastSeen[key];
        const isoDate = new Date(entry.last_seen || entry);
        map[key] = (now - isoDate.getTime()) / 1000;
        return map;
      }, {});
    },

    allFilteredGroupItems() {
      if (!this.groupSearch) return this.groups;
      const s = this.groupSearch.toLowerCase();
      return this.groups.filter(g => g.name.toLowerCase().includes(s));
    },

    filteredGroupItems() {
      return this.allFilteredGroupItems.slice(0, this.groupDisplayCount);
    },

    filteredGroupIds() {
      if (!this.groupSearch) return null;
      return new Set(this.allFilteredGroupItems.map(g => g.id));
    },

    allFilteredUserItems() {
      let result = this.users;

      if (this.filteredGroupIds) {
        result = result.filter(u => u.groups.some(g => this.filteredGroupIds.has(g.id)));
      }

      if (this.userSearch) {
        const s = this.userSearch.toLowerCase();
        result = result.filter(u =>
          u.username.toLowerCase().includes(s) ||
          u.groups.some(g => g.name.toLowerCase().includes(s))
        );
      }

      return result;
    },

    filteredUserItems() {
      return this.allFilteredUserItems.slice(0, this.userDisplayCount);
    }
  },

  watch: {
    allFilteredGroupItems() { this.groupDisplayCount = PAGE_SIZE; },
    allFilteredUserItems()  { this.userDisplayCount  = PAGE_SIZE; }
  },

  mounted() {
    this.loadAll();
    this.$nextTick(() => {
      this.computeHeights();
      window.addEventListener('resize', this.computeHeights);
    });
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.computeHeights);
    this._groupScrollTarget && this._groupScrollTarget.removeEventListener('scroll', this.onGroupScroll);
    this._userScrollTarget  && this._userScrollTarget.removeEventListener('scroll', this.onUserScroll);
  },

  methods: {
    computeWrapperTableHeight(el) {
      if (!el) return 200;
      return Math.max(200, window.innerHeight - el.getBoundingClientRect().top - 20);
    },

    computeHeights() {
      this.groupTableHeight = this.computeWrapperTableHeight(this.$refs.groupTableWrapper);
      this.userTableHeight  = this.computeWrapperTableHeight(this.$refs.userTableWrapper);
      this.$nextTick(() => {
        this.attachScrollTo('.groups-table .v-data-table__wrapper', '_groupScrollTarget', this.onGroupScroll);
        this.attachScrollTo('.users-table .v-data-table__wrapper',  '_userScrollTarget',  this.onUserScroll);
      });
    },

    attachScrollTo(selector, targetKey, handler) {
      const wrapper = this.$el.querySelector(selector);
      if (wrapper && wrapper !== this[targetKey]) {
        this[targetKey] && this[targetKey].removeEventListener('scroll', handler);
        this[targetKey] = wrapper;
        wrapper.addEventListener('scroll', handler);
      }
    },

    onGroupScroll(e) {
      const el = e.target;
      if (el.scrollHeight - el.scrollTop - el.clientHeight < 80) {
        this.groupDisplayCount = Math.min(this.groupDisplayCount + PAGE_SIZE, this.allFilteredGroupItems.length);
      }
    },

    onUserScroll(e) {
      const el = e.target;
      if (el.scrollHeight - el.scrollTop - el.clientHeight < 80) {
        this.userDisplayCount = Math.min(this.userDisplayCount + PAGE_SIZE, this.allFilteredUserItems.length);
      }
    },

    loadAll() {
      this.loadGroups();
      this.loadUsers();
      this.loadBoxStatus();
      this.loadGroupTraffic();
    },

    loadGroups() {
      this.$server.query({ url: 'groups' }).then(data => (this.groups = data)).catch(() => {});
    },

    loadUsers() {
      this.$server.query({ url: 'users' }).then(data => (this.users = data)).catch(() => {});
    },

    /** Loads today's rx/tx totals per group from the server. */
    loadGroupTraffic() {
      this.$server.query({ url: 'groups/traffic' })
        .then(rows => {
          this.groupTraffic = rows.reduce((map, r) => {
            map[r.group_id] = { rx: r.rx_today, tx: r.tx_today };
            return map;
          }, {});
        })
        .catch(() => {});
    },

    loadBoxStatus() {
      Promise.all([
        this.$server.query({ url: 'boxes' }),
        this.$server.query({ url: 'boxes/status' })
      ]).then(([boxes, status]) => {
        this.boxes    = boxes;
        this.lastSeen = status;
      }).catch(() => {});
    },

    groupStats(groupId) {
      const groupBoxes = this.boxes.filter(b => b.group_id === groupId);
      const online = groupBoxes.filter(b => {
        const seconds = this.boxStatusMap[b.wg_public_key];
        return seconds !== undefined && seconds < ONLINE_THRESHOLD_SECONDS;
      }).length;
      return { total: groupBoxes.length, online };
    },

    groupRatio(groupId) {
      const { total, online } = this.groupStats(groupId);
      if (!total) return 0;
      return Math.round((online / total) * 100);
    },

    ratioColor(percent) {
      if (percent >= 80) return 'success';
      if (percent >= 50) return 'warning';
      return 'error';
    },

    openGroupDialog(group) {
      this.isNewGroup  = !group;
      this.groupDraft  = group
        ? { id: group.id, name: group.name, color: group.color || '#607D8B', is_servers: !!group.is_servers }
        : { name: '', color: '#607D8B', is_servers: false };
      this.groupDialog = true;
      this.$nextTick(() => this.$refs.groupForm && this.$refs.groupForm.resetValidation());
    },

    saveGroup() {
      if (!this.$refs.groupForm.validate()) return;
      const data = { name: this.groupDraft.name, color: this.groupDraft.color, is_servers: !!this.groupDraft.is_servers };
      const req  = this.isNewGroup
        ? this.$server.save({ url: 'groups', data })
        : this.$server.update({ url: `groups/${this.groupDraft.id}`, data });
      req.then(() => { this.groupDialog = false; this.loadGroups(); })
         .catch(err => this.$server.defaultErrorHandler(err));
    },

    deleteGroup(group) {
      this.$server
        .delete({ url: `groups/${group.id}` })
        .then(this.loadGroups)
        .catch(err => this.$server.defaultErrorHandler(err));
    },

    openUserDialog(user) {
      this.showPassword = false;
      this.isNewUser  = !user;
      this.userDraft  = user
        ? { id: user.id, username: user.username, password: '', groupIds: user.groups.map(g => g.id) }
        : { id: null, username: '', password: '', groupIds: [] };
      this.userDialog = true;
      this.$nextTick(() => this.$refs.userForm && this.$refs.userForm.resetValidation());
    },

    saveUser() {
      if (!this.$refs.userForm.validate()) return;
      const data = { groupIds: this.userDraft.groupIds };
      if (this.isNewUser) {
        data.username = this.userDraft.username;
        data.password = this.userDraft.password;
      } else if (this.userDraft.password) {
        data.password = this.userDraft.password;
      }
      const req = this.isNewUser
        ? this.$server.save({ url: 'users', data })
        : this.$server.update({ url: `users/${this.userDraft.id}`, data });
      req.then(() => { this.userDialog = false; this.loadUsers(); })
         .catch(err => this.$server.defaultErrorHandler(err));
    },

    deleteUser(user) {
      this.$server
        .delete({ url: `users/${user.id}` })
        .then(this.loadUsers)
        .catch(err => this.$server.defaultErrorHandler(err));
    }
  }
};
</script>

<style scoped>
.gu-layout {
  height: calc(100vh - 56px);
  display: grid;
  grid-template-columns: 1.5fr 1fr;
  grid-template-rows: 1fr;
  padding: 20px;
  gap: 16px;
  overflow: hidden;
}

.gu-panel {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.gu-card {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.tab-toolbar      { flex-shrink: 0; }
.tab-table-wrapper{ flex: 1; min-height: 0; }

.clickable-row    { cursor: pointer; }
.gap-2            { gap: 8px; }

.group-preview-bar {
  width: 100%; padding: 8px 12px; border-radius: 6px;
  color: #fff; font-weight: 600; font-size: 14px;
  text-align: center; letter-spacing: .02em;
  min-height: 36px; display: flex; align-items: center; justify-content: center;
}
</style>
